"use client";

import { motion } from "motion/react";
import { useEffect, useId, useRef, useState } from "react";
import { cn } from "@/lib/utils";

type Props = {
  width?: number;
  height?: number;
  x?: number;
  y?: number;
  strokeDasharray?: number | string;
  numSquares?: number;
  className?: string;
  maxOpacity?: number;
  duration?: number;
  repeatDelay?: number;
};

type Square = { id: number; pos: [number, number] };

function pickPos(
  dimsW: number,
  dimsH: number,
  cellW: number,
  cellH: number
): [number, number] {
  return [
    Math.floor((Math.random() * dimsW) / cellW),
    Math.floor((Math.random() * dimsH) / cellH),
  ];
}

function generateSquares(
  count: number,
  dimsW: number,
  dimsH: number,
  cellW: number,
  cellH: number
): Square[] {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    pos: dimsW > 0 && dimsH > 0 ? pickPos(dimsW, dimsH, cellW, cellH) : [0, 0],
  }));
}

export function AnimatedGridPattern({
  width = 40,
  height = 40,
  x = -1,
  y = -1,
  strokeDasharray = 0,
  numSquares = 50,
  className,
  maxOpacity = 0.5,
  duration = 4,
  repeatDelay = 0.5,
}: Props) {
  const id = useId();
  const containerRef = useRef<SVGSVGElement>(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });
  const [squares, setSquares] = useState<Square[]>(() =>
    generateSquares(numSquares, 0, 0, width, height)
  );

  useEffect(() => {
    if (dims.w > 0 && dims.h > 0) {
      setSquares(generateSquares(numSquares, dims.w, dims.h, width, height));
    }
  }, [dims, numSquares, width, height]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setDims({ w: entry.contentRect.width, h: entry.contentRect.height });
      }
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  const updateSquare = (sid: number) => {
    setSquares((curr) =>
      curr.map((sq) =>
        sq.id === sid
          ? { ...sq, pos: pickPos(dims.w, dims.h, width, height) }
          : sq
      )
    );
  };

  return (
    <svg
      ref={containerRef}
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute inset-0 h-full w-full fill-current/30 stroke-current/30",
        className
      )}
    >
      <defs>
        <pattern
          id={id}
          width={width}
          height={height}
          patternUnits="userSpaceOnUse"
          x={x}
          y={y}
        >
          <path
            d={`M.5 ${height}V.5H${width}`}
            fill="none"
            strokeDasharray={strokeDasharray}
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${id})`} />
      <svg x={x} y={y} className="overflow-visible">
        {squares.map(({ pos: [px, py], id: sid }, idx) => (
          <motion.rect
            initial={{ opacity: 0 }}
            animate={{ opacity: maxOpacity }}
            transition={{
              duration,
              repeat: Infinity,
              delay: idx * 0.1,
              repeatType: "reverse",
              repeatDelay,
            }}
            onAnimationComplete={() => updateSquare(sid)}
            key={`${px}-${py}-${idx}`}
            width={width - 1}
            height={height - 1}
            x={px * width + 1}
            y={py * height + 1}
            fill="currentColor"
            strokeWidth={0}
          />
        ))}
      </svg>
    </svg>
  );
}
